import React from 'react';
import { TimesheetDay } from 'api/report/types';
import { buildTimesheetPayload } from 'report/logic/timesheet/payload';
import { ReportViewProps } from '../types';
import { useTimesheetDraft } from './TimesheetDraftContext';
import { TimesheetSettingsType } from 'api/customer/types';
import {
    TravelsheetKeyType,
    calculateSumPerColumn,
    calculateTotalReportedTime,
} from 'report/logic/timesheet/calculations';
import { addSickHours } from 'report/logic/timesheet/sickness';
import DesktopView from './desktop/View';
import MobileView from './mobile/View';
import BlockRoute from 'components/interface/BlockRoute/BlockRoute';
import { formatReportHours } from 'utils/utils';

export default function TimesheetComponent({
    readOnly,
    loading,
    success,
    error,
    isMobile,
    onSubmit,
    formID,
    disabled,
    showSickHours,
}: ReportViewProps): JSX.Element {
    const {
        report,
        hasChanges,
        timesheet,
        setTimesheet,
        enableSickHours: draftSickHours,
        setEnableSickHours,
        useSplitTextfields,
    } = useTimesheetDraft();
    const canSubmit = !readOnly;
    const enableSickHours = readOnly
        ? (showSickHours ?? draftSickHours)
        : draftSickHours;
    const { template } = report;
    const timesheetSetting = timesheet.timesheetSetting;
    const allowTravelsheet = timesheet.allowTravelsheet;
    const summary = React.useMemo(
        () =>
            readOnly
                ? enableSickHours
                    ? timesheet.summary
                    : timesheet.summaryNormalHours
                : calculateSumPerColumn(timesheet, template.data),
        [readOnly, enableSickHours, timesheet, template.data]
    );
    const totalHours = React.useMemo(
        () =>
            readOnly
                ? formatReportHours(
                      enableSickHours
                          ? report.summary.totalHours
                          : report.summary.totalNonSickHours
                  )
                : calculateTotalReportedTime(timesheet, timesheetSetting),
        [readOnly, enableSickHours, report.summary, timesheet, timesheetSetting]
    );

    const handleUpdateTimesheetDay = (
        updateFunc: (day: TimesheetDay) => TimesheetDay,
        index: number
    ) => {
        setTimesheet(prev => {
            if (!prev.days) {
                return prev;
            }
            return {
                ...prev,
                days: prev.days.map((day, dayIndex) =>
                    dayIndex === index ? updateFunc(day) : day
                ),
            };
        });
    };

    const handleTravelsheetFieldChange =
        (key: TravelsheetKeyType, index: number) => (value: string) => {
            setTimesheet(prev => {
                if (!prev || !prev.travelsheet) {
                    return prev;
                }
                const t = { ...prev, travelsheet: { ...prev.travelsheet } };
                let values = t?.travelsheet[key]?.slice() || [];
                if (values.length < 1) {
                    // Hardcoded like the column count for the travelsheet
                    values = Array(6).fill('');
                }
                values[index] = value;
                t.travelsheet[key] = values;
                return t;
            });
        };

    const handleNotesFieldChange = (value: string) => {
        setTimesheet(prev => ({
            ...prev,
            notes: value,
        }));
    };

    const handleEnableSickHours = (value: boolean) => {
        setEnableSickHours(value);
        if (value) {
            setTimesheet(previous =>
                addSickHours(previous, template.data, previous.timesheetSetting)
            );
        }
    };

    const commentContainsSickHints = React.useMemo(() => {
        if (!timesheet?.notes || !canSubmit) {
            return false;
        }
        const regex = /sjuk(?!vård|hus)/;
        return regex.test(timesheet?.notes?.toLowerCase() || '');
    }, [timesheet?.notes, canSubmit]);

    // the days that contains sick hours that are the same as the normal hours
    const sickHoursDuplicateDays = React.useMemo(() => {
        if (!enableSickHours || !canSubmit) {
            return [];
        }
        const days: TimesheetDay[] = [];
        for (const day of timesheet.days ?? []) {
            if (timesheetSetting === TimesheetSettingsType.START_STOP_TIME) {
                if (day.intervals.length === 2) {
                    const first = day.intervals[0];
                    const second = day.intervals[1];
                    // this will check if the first and second is same "type" of hours. If it is, we skip this day
                    // we want one sick and one normal
                    if (first.isSickHours && second.isSickHours) {
                        continue;
                    }
                    if (
                        first.start === '' ||
                        first.stop === '' ||
                        second.start === '' ||
                        second.stop === ''
                    ) {
                        continue;
                    }

                    if (
                        first.start === second.start &&
                        first.stop === second.stop
                    ) {
                        days.push(day);
                    }
                }
            } else {
                if (day.sickHours?.length === 0) {
                    continue;
                }

                for (let j = 0; j < day.sickHours.length; j++) {
                    const sickHour = parseFloat(day.sickHours[j]) || 0;
                    const normal = parseFloat(day.hours[j]) || 0;
                    if (sickHour === normal && sickHour !== 0) {
                        days.push(day);
                        break;
                    }
                }
            }
        }

        return days;
    }, [timesheet.days, timesheetSetting, enableSickHours, canSubmit]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (readOnly || disabled || loading) {
            return;
        }
        void onSubmit(
            buildTimesheetPayload(timesheet, template.id, enableSickHours)
        );
    };

    return (
        <>
            <form
                id={formID}
                noValidate
                onSubmit={handleSubmit}
                onKeyDown={event => {
                    // Enter keeps editing fields; submission is an explicit button action.
                    if (
                        event.key === 'Enter' &&
                        event.target instanceof HTMLInputElement
                    ) {
                        event.preventDefault();
                    }
                }}>
                {!isMobile ? (
                    <DesktopView
                        timesheet={timesheet}
                        totalHours={totalHours}
                        template={template}
                        summary={summary}
                        timesheetSetting={timesheetSetting}
                        canSubmit={canSubmit}
                        disabled={disabled}
                        useSplitTextfields={useSplitTextfields}
                        loading={loading}
                        error={error}
                        allowTravelsheet={allowTravelsheet}
                        enableSickHours={enableSickHours}
                        onEnableSickHours={handleEnableSickHours}
                        onUpdateTimesheetDay={handleUpdateTimesheetDay}
                        onTravelsheetFieldChange={handleTravelsheetFieldChange}
                        onNotesFieldChange={handleNotesFieldChange}
                        commentContainsSickHints={commentContainsSickHints}
                        sickHoursDuplicateDays={sickHoursDuplicateDays}
                    />
                ) : (
                    <MobileView
                        timesheet={timesheet}
                        totalHours={totalHours}
                        template={template}
                        timesheetSetting={timesheetSetting}
                        canSubmit={canSubmit}
                        disabled={disabled}
                        useSplitTextfields={useSplitTextfields}
                        loading={loading}
                        error={error}
                        allowTravelsheet={allowTravelsheet}
                        enableSickHours={enableSickHours}
                        onEnableSickHours={handleEnableSickHours}
                        onUpdateTimesheetDay={handleUpdateTimesheetDay}
                        onTravelsheetFieldChange={handleTravelsheetFieldChange}
                        onNotesFieldChange={handleNotesFieldChange}
                        commentContainsSickHints={commentContainsSickHints}
                        sickHoursDuplicateDays={sickHoursDuplicateDays}
                    />
                )}
            </form>
            <BlockRoute
                shouldBlockNavigation={canSubmit && !success && hasChanges}
                isMobile={isMobile}
            />
        </>
    );
}
